import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { initMetaPixel } from "@/lib/meta-pixel";

type ConsentState = "accepted" | "rejected" | null;

export default function CookieConsent() {
  const [consentState, setConsentState] = useState<ConsentState>("accepted"); // prevent flash

  useEffect(() => {
    const stored = localStorage.getItem("cookieConsent") as ConsentState;
    if (stored === "accepted" || stored === "rejected") {
      setConsentState(stored);
      if (stored === "accepted") initMetaPixel();
    } else {
      setConsentState(null); // show banner
    }
  }, []);

  const handleConsent = (choice: "accepted" | "rejected") => {
    localStorage.setItem("cookieConsent", choice);
    setConsentState(choice);
    if (choice === "accepted") {
      initMetaPixel();
      window.dispatchEvent(new CustomEvent("cookieConsentAccepted"));
    }
  };

  const variants = {
    hidden: { y: 100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
    exit: {
      y: 100,
      opacity: 0,
      transition: { duration: 0.3 },
    },
  };

  return (
    <AnimatePresence>
      {consentState === null && (
        <motion.div
          className="fixed bottom-0 left-0 right-0 bg-[#0A1228]/95 backdrop-blur-sm border-t border-white/10 p-4 md:p-6 z-50"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={variants}
        >
          <div className="container mx-auto max-w-4xl">
            <p className="text-white/80 text-sm mb-4">
              Questo sito utilizza cookie tecnici necessari al funzionamento e cookie
              di terze parti (Microsoft Clarity, Meta Pixel) per analisi e marketing.
              Questi cookie vengono attivati solo con il tuo consenso
              esplicito. Per maggiori informazioni, consulta la{" "}
              <Link
                href="/cookie-policy"
                className="text-primary hover:underline"
              >
                cookie policy
              </Link>
              .
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-end">
              <Button
                variant="outline"
                onClick={() => handleConsent("rejected")}
                className="border-white/20 text-white/70 hover:bg-white/10 hover:text-white font-montserrat text-sm"
              >
                Solo necessari
              </Button>
              <Button
                onClick={() => handleConsent("accepted")}
                className="bg-primary text-white font-montserrat font-medium hover:bg-primary/90 text-sm"
              >
                Accetta tutti
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
